/**
 * SaaS Workspace Service — Workflow Manager Commercial Platform
 *
 * All Supabase calls for workspaces, plans, subscriptions, usage.
 * Components must NOT call Supabase directly — use this service.
 */

import { supabase } from '../lib/supabaseClient';
import type {
  SaasWorkspace,
  SaasPlan,
  SaasSubscription,
  SaasInvoice,
  UsageMetric,
  SaasDashboardStats,
  SaasMetrics,
  MetricKey,
  PlanSlug,
  WorkspaceStatus,
  GrowthEvent,
  FeatureFlag,
  WorkspaceFeatureFlag,
  Notification,
  SystemAlert,
  ActivityFeedItem,
  WorkspaceLimitCheck,
} from '../types/saasTypes';

// ─── Plans ────────────────────────────────────────────────────

export async function getPlans(): Promise<SaasPlan[]> {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) throw new Error(error.message);
  return (data ?? []) as SaasPlan[];
}

export async function getPlanBySlug(slug: PlanSlug): Promise<SaasPlan | null> {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .single();

  if (error) return null;
  return data as SaasPlan;
}

// ─── Workspaces ───────────────────────────────────────────────

export async function getUserWorkspaces(userId: string): Promise<SaasWorkspace[]> {
  const { data, error } = await supabase
    .from('workspaces')
    .select(`
      *,
      plan:plans(*)
    `)
    .or(`owner_user_id.eq.${userId},workspace_members.user_id.eq.${userId}`)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as SaasWorkspace[];
}

/** Get workspaces the user is a member of (more reliable join) */
export async function getMemberWorkspaces(userId: string): Promise<SaasWorkspace[]> {
  const { data, error } = await supabase
    .from('workspace_members')
    .select(`
      workspace:workspaces(*, plan:plans(*))
    `)
    .eq('user_id', userId);

  if (error) throw new Error(error.message);
  return ((data ?? []) as { workspace: SaasWorkspace }[])
    .map((r) => r.workspace)
    .filter(Boolean);
}

export async function getWorkspace(workspaceId: string): Promise<SaasWorkspace> {
  const { data, error } = await supabase
    .from('workspaces')
    .select(`*, plan:plans(*)`)
    .eq('id', workspaceId)
    .single();

  if (error) throw new Error(error.message);
  return data as SaasWorkspace;
}

export async function createWorkspace(params: {
  name: string;
  slug: string;
  ownerUserId: string;
  planId?: string;
}): Promise<SaasWorkspace> {
  const { data, error } = await supabase
    .from('workspaces')
    .insert({
      name: params.name,
      slug: params.slug,
      owner_user_id: params.ownerUserId,
      plan_id: params.planId ?? null,
      status: 'trial' as WorkspaceStatus,
    })
    .select(`*, plan:plans(*)`)
    .single();

  if (error) throw new Error(error.message);
  return data as SaasWorkspace;
}

export async function updateWorkspace(
  workspaceId: string,
  fields: Partial<Pick<SaasWorkspace, 'name' | 'slug' | 'billing_email' | 'status' | 'metadata'>>
): Promise<void> {
  const { error } = await supabase
    .from('workspaces')
    .update(fields)
    .eq('id', workspaceId);

  if (error) throw new Error(error.message);
}

/** Provision workspace + org link + trial subscription in one DB call */
export async function provisionWorkspace(params: {
  orgId: string;
  orgName: string;
  ownerId: string;
  planSlug?: PlanSlug;
}): Promise<string> {
  const { data, error } = await supabase.rpc('provision_workspace_for_organization', {
    p_org_id: params.orgId,
    p_org_name: params.orgName,
    p_owner_id: params.ownerId,
    p_plan_slug: params.planSlug ?? 'free',
  });

  if (error) throw new Error(error.message);
  return data as string; // returns workspace_id UUID
}

// ─── Subscriptions ────────────────────────────────────────────

export async function getSubscription(workspaceId: string): Promise<SaasSubscription | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(`*, plan:plans(*)`)
    .eq('workspace_id', workspaceId)
    .single();

  if (error) return null;
  return data as SaasSubscription;
}

export async function getAllSubscriptions(): Promise<SaasSubscription[]> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(`*, plan:plans(*), workspace:workspaces(name, slug, status)`)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as SaasSubscription[];
}

export async function upgradePlan(params: {
  workspaceId: string;
  planId: string;
  billingCycle: 'monthly' | 'annual';
  amount: number;
}): Promise<void> {
  // Update workspace plan
  const { error: wsError } = await supabase
    .from('workspaces')
    .update({ plan_id: params.planId, status: 'active' as WorkspaceStatus })
    .eq('id', params.workspaceId);

  if (wsError) throw new Error(wsError.message);

  // Upsert subscription
  const { error: subError } = await supabase
    .from('subscriptions')
    .upsert(
      {
        workspace_id: params.workspaceId,
        plan_id: params.planId,
        status: 'active',
        billing_cycle: params.billingCycle,
        amount: params.amount,
        current_period_start: new Date().toISOString(),
        current_period_end: new Date(
          Date.now() + (params.billingCycle === 'annual' ? 365 : 30) * 86400_000
        ).toISOString(),
      },
      { onConflict: 'workspace_id' }
    );

  if (subError) throw new Error(subError.message);
}

// ─── Invoices ─────────────────────────────────────────────────

export async function getInvoices(workspaceId: string): Promise<SaasInvoice[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('issued_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as SaasInvoice[];
}

export async function getAllInvoices(): Promise<SaasInvoice[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select(`*, workspace:workspaces(name, slug)`)
    .order('issued_at', { ascending: false })
    .limit(200);

  if (error) throw new Error(error.message);
  return (data ?? []) as SaasInvoice[];
}

// ─── Usage Metrics ────────────────────────────────────────────

export async function getUsageMetrics(workspaceId: string): Promise<UsageMetric[]> {
  const now = new Date();
  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('usage_metrics')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('period_start', periodStart)
    .order('metric_key');

  if (error) throw new Error(error.message);
  return (data ?? []) as UsageMetric[];
}

export async function incrementUsageMetric(
  workspaceId: string,
  metricKey: MetricKey,
  delta = 1
): Promise<void> {
  const { error } = await supabase.rpc('upsert_usage_metric', {
    p_workspace_id: workspaceId,
    p_metric_key: metricKey,
    p_delta: delta,
  });

  if (error) throw new Error(error.message);
}

// ─── SaaS Admin Stats ─────────────────────────────────────────

export async function getSaasDashboardStats(): Promise<SaasDashboardStats> {
  const { data, error } = await supabase
    .from('saas_dashboard_stats')
    .select('*')
    .single();

  if (error) {
    // View may not exist if migration hasn't been applied yet — return empty stats
    return {
      total_workspaces: 0,
      trial_workspaces: 0,
      active_workspaces: 0,
      suspended_workspaces: 0,
      cancelled_workspaces: 0,
      expired_trials: 0,
      mrr: 0,
      mrr_annual: 0,
      active_users: 0,
    };
  }

  return {
    total_workspaces: Number(data.total_workspaces ?? 0),
    trial_workspaces: Number(data.trial_workspaces ?? 0),
    active_workspaces: Number(data.active_workspaces ?? 0),
    suspended_workspaces: Number(data.suspended_workspaces ?? 0),
    cancelled_workspaces: Number(data.cancelled_workspaces ?? 0),
    expired_trials: Number(data.expired_trials ?? 0),
    mrr: Number(data.mrr ?? 0),
    mrr_annual: Number(data.mrr_annual ?? 0),
    active_users: Number(data.active_users ?? 0),
  };
}

/** Get all workspaces for admin management */
export async function adminGetAllWorkspaces(): Promise<
  (SaasWorkspace & { member_count?: number })[]
> {
  const { data, error } = await supabase
    .from('workspaces')
    .select(`
      *,
      plan:plans(name, slug, price_month)
    `)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as SaasWorkspace[];
}

export async function adminUpdateWorkspaceStatus(
  workspaceId: string,
  status: WorkspaceStatus
): Promise<void> {
  const { error } = await supabase
    .from('workspaces')
    .update({ status })
    .eq('id', workspaceId);

  if (error) throw new Error(error.message);
}

// ─── Slug validation ──────────────────────────────────────────

export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 48);
}

export async function isSlugAvailable(slug: string): Promise<boolean> {
  const { data } = await supabase
    .from('workspaces')
    .select('id')
    .eq('slug', slug)
    .maybeSingle();
  return data === null;
}

// ─── Enterprise SaaS Metrics ──────────────────────────────────

export async function getSaasMetrics(): Promise<SaasMetrics> {
  const { data, error } = await supabase
    .from('saas_metrics')
    .select('*')
    .single();

  if (error) {
    return {
      total_workspaces: 0, trial_workspaces: 0, active_workspaces: 0,
      suspended_workspaces: 0, cancelled_workspaces: 0, expired_trials: 0,
      mrr: 0, mrr_annual: 0, active_users: 0,
      total_mrr: 0, total_arr: 0, total_users: 0,
      paid_subscriptions: 0, trial_subscriptions: 0, churn_30d: 0,
      active_alerts: 0, errors_24h: 0,
    };
  }
  const n = (v: unknown) => Number(v ?? 0);
  return {
    total_workspaces: n(data.total_workspaces),
    trial_workspaces: n(data.trial_workspaces),
    active_workspaces: n(data.active_workspaces),
    suspended_workspaces: n(data.suspended_workspaces),
    cancelled_workspaces: n(data.cancelled_workspaces),
    expired_trials: n(data.expired_trials),
    mrr: n(data.mrr_monthly),
    mrr_annual: n(data.mrr_annual),
    active_users: n(data.active_users),
    total_mrr: n(data.total_mrr),
    total_arr: n(data.total_arr),
    total_users: n(data.total_users),
    paid_subscriptions: n(data.paid_subscriptions),
    trial_subscriptions: n(data.trial_subscriptions),
    churn_30d: n(data.churn_30d),
    active_alerts: n(data.active_alerts),
    errors_24h: n(data.errors_24h),
  };
}

// ─── Growth Events ────────────────────────────────────────────

export async function logGrowthEvent(
  workspaceId: string | null,
  userId: string | null,
  eventType: string,
  metadata: Record<string, unknown> = {}
): Promise<void> {
  await supabase.from('growth_events').insert({
    workspace_id: workspaceId,
    user_id: userId,
    event_type: eventType,
    metadata,
  });
}

export async function getGrowthEvents(limit = 100): Promise<GrowthEvent[]> {
  const { data, error } = await supabase
    .from('growth_events')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as GrowthEvent[];
}

// ─── Feature Flags ────────────────────────────────────────────

export async function getFeatureFlags(): Promise<FeatureFlag[]> {
  const { data, error } = await supabase
    .from('feature_flags')
    .select('*')
    .order('category', { ascending: true });
  if (error) return [];
  return (data ?? []) as FeatureFlag[];
}

export async function getWorkspaceFeatureFlags(workspaceId: string): Promise<WorkspaceFeatureFlag[]> {
  const { data, error } = await supabase
    .from('workspace_feature_flags')
    .select('*')
    .eq('workspace_id', workspaceId);
  if (error) return [];
  return (data ?? []) as WorkspaceFeatureFlag[];
}

export async function toggleWorkspaceFeatureFlag(
  workspaceId: string,
  flagKey: string,
  enabled: boolean,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from('workspace_feature_flags')
    .upsert({
      workspace_id: workspaceId,
      flag_key: flagKey,
      enabled,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'workspace_id,flag_key' });
  if (error) throw new Error(error.message);
}

// ─── Notifications ────────────────────────────────────────────

export async function getNotifications(userId: string, limit = 50): Promise<Notification[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as Notification[];
}

export async function getUnreadNotificationCount(userId: string): Promise<number> {
  const { count } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false);
  return count ?? 0;
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await supabase
    .from('notifications')
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('id', notificationId);
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  await supabase
    .from('notifications')
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('read', false);
}

// ─── System Alerts ────────────────────────────────────────────

export async function getActiveAlerts(): Promise<SystemAlert[]> {
  const { data, error } = await supabase
    .from('system_alerts')
    .select('*')
    .eq('resolved', false)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) return [];
  return (data ?? []) as SystemAlert[];
}

export async function acknowledgeAlert(alertId: string, userId: string): Promise<void> {
  await supabase
    .from('system_alerts')
    .update({ acknowledged: true, acknowledged_by: userId, acknowledged_at: new Date().toISOString() })
    .eq('id', alertId);
}

export async function resolveAlert(alertId: string): Promise<void> {
  await supabase
    .from('system_alerts')
    .update({ resolved: true, resolved_at: new Date().toISOString() })
    .eq('id', alertId);
}

// ─── Activity Feed ────────────────────────────────────────────

export async function getActivityFeed(workspaceId: string, limit = 30): Promise<ActivityFeedItem[]> {
  const { data, error } = await supabase
    .from('activity_feed')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as ActivityFeedItem[];
}

export async function logActivity(params: {
  workspaceId: string;
  userId: string | null;
  eventType: string;
  title: string;
  description?: string;
  entityType?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  await supabase.from('activity_feed').insert({
    workspace_id: params.workspaceId,
    user_id: params.userId,
    event_type: params.eventType,
    title: params.title,
    description: params.description ?? null,
    entity_type: params.entityType ?? null,
    entity_id: params.entityId ?? null,
    metadata: params.metadata ?? {},
  });
}

// ─── Workspace Limits ─────────────────────────────────────────

export async function checkWorkspaceLimit(
  workspaceId: string,
  limitKey: 'users' | 'workflows' | 'projects'
): Promise<WorkspaceLimitCheck> {
  const { data, error } = await supabase.rpc('check_workspace_limit', {
    p_workspace_id: workspaceId,
    p_limit_key: limitKey,
  });
  if (error) return { allowed: true, limit: -1, current: 0, remaining: -1 };
  return data as WorkspaceLimitCheck;
}
